import {sweetAPI} from 'app/store/sweet-api';
import {
  convertLocationParamsToFetchParams,
  providesTagsByURL,
  showLoadingBar,
  showMessages,
} from 'app/store/sweet-api/utils';
import {NOTIFICATIONS_API, SUBSCRIBERS_API_URL, THREAD_API_URL} from 'app/urls';
import {getContentType} from 'app/utils/content-type';
import {gt} from 'app/utils/i18n';
import {
  NOTIFICATION_TAG,
  SUBSCRIBERS_TAG,
  SUBSCRIPTION_STATUS_TAG,
} from 'app/utils/rtk-tags';

import type {
  NotificationsFetchParamsType,
  NotificationsLocationParamsType,
  NotificationType,
  PreviewEmailParamsType,
  PreviewEmailResponseType,
} from './types';
import type {SubscriptableType} from 'app/notifly/actions';
import type {BasicUserSerializedType, ListResponseType} from 'sweet-types';

export type SubscribersFetchParamsType = {
  object_id: number;
  page?: string | number;
  content_type: SubscriptableType;
};

export type SubscribeParamsType = Pick<
  SubscribersFetchParamsType,
  'object_id' | 'content_type'
>;

type SubUnsubParamsType = SubscribeParamsType & {
  url: string;
};

export const extendedAPI = sweetAPI
  .enhanceEndpoints({
    addTagTypes: [NOTIFICATION_TAG, SUBSCRIBERS_TAG, SUBSCRIPTION_STATUS_TAG],
  })
  .injectEndpoints({
    endpoints: (build) => ({
      notifications: build.query<
        ListResponseType<NotificationType>,
        NotificationsFetchParamsType
      >({
        query: (params) => ({
          url: NOTIFICATIONS_API,
          params,
        }),
        onQueryStarted: (args, api) => {
          showLoadingBar(args, api);
          showMessages({
            onError: () => gt('Could not fetch notifications.'),
          })(args, api);
        },
        providesTags: providesTagsByURL(NOTIFICATION_TAG),
      }),
      subscribers: build.query<
        ListResponseType<BasicUserSerializedType>,
        SubscribersFetchParamsType
      >({
        query: (params) => ({
          url: SUBSCRIBERS_API_URL,
          params,
        }),
        onQueryStarted: async (args, api) => {
          showMessages({
            onError: () => gt('Could not load subscribers.'),
          })(args, api);
        },
        providesTags: () => [{type: SUBSCRIBERS_TAG, id: 'list'}],
      }),
      subscriptionStatus: build.query<
        ListResponseType<SubUnsubParamsType>,
        SubscribeParamsType
      >({
        query: (params) => ({
          url: THREAD_API_URL,
          params,
        }),
        providesTags: () => [{type: SUBSCRIPTION_STATUS_TAG, id: 'list'}],
      }),
      subscribe: build.mutation<SubUnsubParamsType, SubscribeParamsType>({
        query: ({object_id, content_type}) => ({
          url: THREAD_API_URL,
          method: 'POST',
          body: {object_id, content_type},
        }),
        invalidatesTags: () => [
          {type: SUBSCRIBERS_TAG, id: 'list'},
          {type: SUBSCRIPTION_STATUS_TAG, id: 'list'},
        ],
        onQueryStarted: async (args, api) => {
          showMessages({
            onSuccess: ({content_type}) =>
              gt(
                `You are now subscribed to notifications for this ${
                  getContentType(content_type).name
                }.`
              ),
            onError: () => gt('Could subscribe to document'),
          })(args, api);
        },
      }),
      unsubscribe: build.mutation<unknown, SubUnsubParamsType>({
        query: ({url}) => ({
          url,
          method: 'DELETE',
        }),
        invalidatesTags: () => [
          {type: SUBSCRIBERS_TAG, id: 'list'},
          {type: SUBSCRIPTION_STATUS_TAG, id: 'list'},
        ],
        onQueryStarted: async (args, api) => {
          showMessages({
            onSuccess: ({content_type}) =>
              gt(
                `You have unsubscribed from notifications for this ${
                  getContentType(content_type).name
                }.`
              ),
            onError: () => gt('Could not unsubscribe from document'),
          })(args, api);
        },
      }),
      updateNotification: build.mutation<NotificationType, NotificationType>({
        query: (notification) => ({
          url: notification.url,
          method: 'PATCH',
          body: notification,
        }),
        onQueryStarted: async (args, api) => {
          showMessages({
            onError: () => gt('Could not update the notification.'),
          })(args, api);
        },
        invalidatesTags: (notification) => [
          {type: NOTIFICATION_TAG, id: 'list'},
          {type: NOTIFICATION_TAG, id: notification?.url ?? ''},
        ],
      }),
      previewEmail: build.mutation<
        PreviewEmailResponseType,
        PreviewEmailParamsType
      >({
        query: ({content_type, verb, dynamic_data}) => ({
          url: `${NOTIFICATIONS_API}preview_email/`,
          method: 'POST',
          body: {
            content_type: content_type,
            verb: verb,
            dynamic_data: dynamic_data,
          },
        }),
        onQueryStarted: async (args, api) => {
          showMessages({
            onError: () =>
              gt('Could not generate email preview at the moment.'),
          })(args, api);
        },
      }),
    }),
  });

export const {
  useNotificationsQuery: useNotifications,
  useSubscribersQuery: useSubscribers,
  useSubscriptionStatusQuery: useSubscriptionStatus,
  useSubscribeMutation: useSubscribe,
  useUnsubscribeMutation: useUnsubscribe,
  useUpdateNotificationMutation: useUpdateNotification,
  usePreviewEmailMutation: usePreviewEmail,
} = extendedAPI;

export const convertNotificationLocationParamsToFetchParams = (
  params: NotificationsLocationParamsType
): NotificationsFetchParamsType => ({
  ...convertLocationParamsToFetchParams(params),
  web_visible: params.web_visible != null ? params.web_visible : 'true',
  is_all: params.is_all != null ? params.is_all : 'true',
});
